<script setup lang="ts">
import type { BundledGoodsInfo, GoodsQnaInfo } from '~/types/goods/goods-detail'
import { popupHandler } from '~/composables/popup/popup-handler'
import { extractPayloadTransform } from '~/transforms/goods/goods-transform'
import type { ShopApiResponse } from '~/types/common/shop-api-response'
import { getGoodsQnAInfo, getGoodsQnAList } from '~/services/goods/qnA-service'
import { isNotEmptyArray } from '~/utils/shop-common'

const { loginDrvtModalOpen } = popupHandler()
const { isMobile } = useDevice()
const { $filter } = useNuxtApp()

const { goodsNo: propsGoodsNo, bundleGoodsNo, prRelGoodsInfo } = defineProps<{
  goodsNo: string
  bundleGoodsNo?: string
  prRelGoodsInfo?: BundledGoodsInfo[]
}>()
const tabData = defineModel<object>({ default: null })

const goodsNo = ref(propsGoodsNo)
const qnAInfo = ref()
const totalCnt = ref()
const infinityScrollList = ref(null)
const paging = reactive({ pageNo: 1, pageSize: 20 })

const { data, refresh } = useLocaAsyncData<ShopApiResponse<GoodsQnaInfo>, GoodsQnaInfo>
(`getGoodsQnAInfo:${goodsNo.value}`, () => getGoodsQnAInfo(goodsNo.value, bundleGoodsNo), { transform: extractPayloadTransform }, false)

const fullAempCellNumber = computed(() => {
  const { aempTelRgnNo, aempTelTxnoNo, aempTelEndNo } = qnAInfo.value
  return aempTelEndNo ? `${aempTelRgnNo}-${aempTelTxnoNo}-${aempTelEndNo}` : `${aempTelRgnNo}-${aempTelTxnoNo}`
})

watch(data, () => {
  init()
})

watch(
  goodsNo, async () => {
    await refresh()
  },
)

async function fetchGoodsQnAList() {
  return await getGoodsQnAList(goodsNo.value, paging).then(res => res.payload)
}

function init() {
  qnAInfo.value = data.value?.goods
  totalCnt.value = data.value?.totalCnt
  if (tabData.value) {
    tabData.value.updateTitle(isEmptyValue(bundleGoodsNo) ? totalCnt.value : data.value.bundleGoodsTotalCnt)
  }
  clearList()
}

const clearList = () => {
  if (infinityScrollList.value)
    infinityScrollList.value.clearList()
}

function clickInquiryBtn() {
  if (!loginDrvtModalOpen(true)) return
  const { open: openQnARegister } = useQnARegisterModal(goodsNo.value, qnAInfo.value.entrNo, async () => {
    await refresh()
    clearList()
  })
  openQnARegister()
}

function setGoodsNo(selectedGoodsNo: string) {
  goodsNo.value = selectedGoodsNo
}

function reloadQna() {
  refresh()
}
</script>

<template>
  <template v-if="isNotEmptyArray(prRelGoodsInfo)">
    <!--  묶음상품  -->
    <GoodsDetailBundleGoodsList
      v-if="isNotEmptyArray(prRelGoodsInfo)"
      :goods-no="goodsNo"
      :pr-rel-goods-info="prRelGoodsInfo"
      @select-goods="setGoodsNo"
    />
    <div class="divider-01" />
    <!--  //묶음상품  -->
  </template>
  <!-- [S] Q&A -->
  <div v-if="!!qnAInfo">
    <div class="py-6 px-4">
      <strong class="text-18 font-medium">{{ qnAInfo.entrNm }}</strong>
      <div class="flex gap-2 mt-4">
        <!-- [DEV] - 전화번호 입력 -->
        <!-- [DEV] 240801 - 전화문의 버튼 PC에서는 미노출  -->
        <a
          v-if="isMobile"
          :href="`tel:${fullAempCellNumber}`"
          type="button"
          class="btn btn-outline text-16 font-medium flex-1"
        >
          <i-normal-call class="w-6 h-6" />
          <span>전화문의</span>
        </a>
        <button
          class="btn btn-outline text-16 font-medium flex-1"
          data-gtm-click
          :data-gtm-body="useGAHandler().generateGTMData({
            label: '판매자 문의하기',
          })"
          @click="clickInquiryBtn"
        >
          <i-normal-pencil class="w-6 h-6" />
          <span>상품문의</span>
        </button>
      </div>
    </div>

    <div class="divider-thick bg-warm-gray-30" />

    <div v-if="totalCnt">
      <div class="pt-6 pb-2 px-4 text-18 font-bold">
        Q&A <span class="text-on-view-03">({{ $filter.numToLocaleStr(totalCnt) }})</span>
      </div>
      <div>
        <ShopInfinityScrollList
          ref="infinityScrollList"
          v-model:paging="paging"
          :async-key="goodsNo"
          :total-size="totalCnt"
          :request="fetchGoodsQnAList"
          :is-cache="false"
        >
          <template #default="{ item }">
            <GoodsDetailQnARow
              :qn-a-data="item"
              @reload-qna="reloadQna"
            />
          </template>
        </ShopInfinityScrollList>
      </div>
    </div>
    <!-- [S] Empty -->
    <ShopGuidance
      v-else
      title="등록된 Q&A가 없어요"
    >
      <template #icon>
        <i-nodata-ask-l class="w-10 h-10" />
      </template>
    </ShopGuidance>
    <!-- [E] Empty -->
  </div>
  <!-- [E] Q&A -->
</template>

<style scoped>

</style>
